import React, { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useDispatch } from "src/store";
import useFetchInterval from "src/ui/hooks/useFetchInterval";

import { createGroupMdmCommand, deleteGroupMdmCommand, fetchGroupMdmCommands } from "@dashboard/devices/api";
import { selectDetailsGroup, selectPaneTab } from "@dashboard/devices/store";
import { MdmCommand } from "@dashboard/devices/types/commands";
import { hasUnfinishedCommands } from "@dashboard/shared/components/SidePanel/shared/CommandTab/CommandTab.utils";
import { workspacesApi } from "@dashboard/workspaces/api";
import { pageIndexOffset } from "@shared/table/Tanstack.utils";
import { showErrorToast, showSuccessToast, showWarningToast } from "@shared/toasts/Toasts";
import { SortingState } from "@tanstack/react-table";

function getSortCriteria(sorting: SortingState) {
    if (!sorting.length) {
        return "date";
    }
    const mapping: Record<string, string> = {
        date: "created",
        statistics: "status",
        command: "caption",
    };
    return mapping[sorting[0].id] || "created";
}
function getSortDirection(sorting: SortingState) {
    if (!sorting.length || sorting[0].desc) {
        return "descending";
    }
    return "ascending";
}

const rowsPerPage = [10, 25, 50];

type GroupCommandContextType = {
    data: MdmCommand[];
    totalCount: number;
    isLoading: boolean;
    deletingId: number | null;
    resendingId: number | null;
    pageIndex: number;
    sorting: SortingState;
    pageSize: number;
    rowsPerPage: number[];
    onCreate: (json: Record<string, unknown>, commandId: number | null) => Promise<void>;
    onDelete: (command: MdmCommand) => Promise<void>;
    setSorting: React.Dispatch<React.SetStateAction<SortingState>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

const GroupCommandContext = React.createContext<GroupCommandContextType>({
    data: [],
    totalCount: 0,
    isLoading: false,
    deletingId: null,
    resendingId: null,
    sorting: [{ id: "date", desc: true }],
    pageIndex: 0,
    pageSize: rowsPerPage[0],
    rowsPerPage,
    onCreate: () => Promise.resolve(),
    onDelete: () => Promise.resolve(),
    setSorting: () => {},
    setPageIndex: () => {},
    setPageSize: () => {},
});

export const GroupCommandProvider = (props: React.PropsWithChildren) => {
    const [sorting, setSorting] = useState<SortingState>([{ id: "date", desc: true }]);
    const [pageIndex, setPageIndex] = useState(0);
    const [pageSize, setPageSize] = useState(rowsPerPage[0]);
    const [data, setData] = useState<MdmCommand[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [isLoading, setIsLoading] = useState(true);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const [resendingId, setResendingId] = useState<number | null>(null);

    const { t } = useTranslation(["common", "sidepanels"]);
    const dispatch = useDispatch();

    const hasUnfinishedCommand = hasUnfinishedCommands(data);

    const group = useSelector(selectDetailsGroup);
    const sortCriteria = getSortCriteria(sorting);
    const sortDirection = getSortDirection(sorting);

    const selectedTab = useSelector(selectPaneTab);
    const isCommandsTab = selectedTab === "COMMANDS";

    const onFetch = useCallback(async () => {
        if (group?.id) {
            setIsLoading(true);

            await fetchGroupMdmCommands({
                groupId: group.id,
                pageSize,
                pageIndex: pageIndex + pageIndexOffset,
                sortCriteria,
                sortDirection,
            })
                .then((response) => {
                    setData(response.list);
                    setTotalCount(response.count);
                })
                .finally(() => {
                    setIsLoading(false);
                });
        }
    }, [group?.id, pageSize, pageIndex, sortCriteria, sortDirection]);
    const onRefresh = useCallback(async () => {
        if (group?.id) {
            try {
                const response = await fetchGroupMdmCommands({
                    groupId: group.id,
                    pageSize,
                    pageIndex: pageIndex + pageIndexOffset,
                    sortCriteria,
                    sortDirection,
                });
                setData(response.list);
                setTotalCount(response.count);
            } catch (_error) {
                setData([]);
                setTotalCount(0);
            }
        }
    }, [group?.id, pageSize, pageIndex, sortCriteria, sortDirection]);
    const onCreate = async (json: Record<string, unknown>, commandId: number | null) => {
        if (group?.id) {
            setResendingId(commandId);
            await createGroupMdmCommand({ groupId: group.id, json })
                .then((newCommands) => {
                    // The backend answers a group with no devices of its own with OK and an empty list, so an
                    // empty array is a successful request that reached nobody - not a failure, and not a success
                    // worth reporting. It carries one row per device actually reached, which is the honest count.
                    if (!newCommands?.length) {
                        showWarningToast(t("sidepanels:groupPanel.toast.commandNoDevices"));
                        return;
                    }

                    setData((prev) => [...newCommands, ...prev]);
                    setTotalCount((prev) => prev + newCommands.length);
                    dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                    showSuccessToast(t("sidepanels:groupPanel.toast.commandQueued", { count: newCommands.length }));
                })
                .catch((err) => {
                    const error = err as Error;
                    showErrorToast(error.message || t("sidepanels:groupPanel.toast.commandSentError"));
                })
                .finally(() => {
                    setResendingId(null);
                });
        }
    };
    const onDelete = async (command: MdmCommand) => {
        if (group?.id) {
            setDeletingId(command.id);
            await deleteGroupMdmCommand({ groupId: group.id, command })
                .then((response) => {
                    if (response) {
                        setData((prev) => prev.filter((cmd) => cmd.id !== command.id));
                        setTotalCount((prev) => prev - 1);
                        dispatch(workspacesApi.util.invalidateTags(["WorkspaceFeatures"]));
                        showSuccessToast(t("sidepanels:groupPanel.toast.commandDeletedSuccess"));
                    }
                })
                .catch((err) => {
                    const error = err as Error;
                    showErrorToast(error.message || t("sidepanels:groupPanel.toast.commandDeletedError"));
                })
                .finally(() => {
                    setDeletingId(null);
                });
        }
    };

    React.useEffect(() => {
        void onFetch();
    }, [onFetch]);

    const wasCommandsTabRef = React.useRef(isCommandsTab);

    React.useEffect(() => {
        const wasCommandsTab = wasCommandsTabRef.current;
        wasCommandsTabRef.current = isCommandsTab;

        // The mount, and every page/sort change, are already fetched by the `onFetch` effect above. Only an
        // actual switch into the Commands tab leaves the parameters untouched and so needs its own refresh.
        if (!wasCommandsTab && isCommandsTab) {
            void onRefresh();
        }
    }, [isCommandsTab, onRefresh]);

    useFetchInterval(onRefresh, {
        frequency: 10000,
        condition: hasUnfinishedCommand && isCommandsTab,
    });

    return (
        <GroupCommandContext.Provider
            value={{
                data,
                totalCount,
                isLoading,
                deletingId,
                resendingId,
                sorting,
                pageIndex,
                pageSize,
                rowsPerPage,
                onCreate,
                onDelete,
                setSorting,
                setPageIndex,
                setPageSize,
            }}
        >
            {props.children}
        </GroupCommandContext.Provider>
    );
};

export const useGroupCommand = () => {
    const context = useContext(GroupCommandContext);
    if (!context) {
        throw new Error("useGroupCommand must be used within a GroupCommandProvider");
    }
    return context;
};
